// Share grid generator for Letter Flick.
//
// Implements SPEC §6.3 — multi-row attempt snapshots, one row per undo (plus
// the final state). The share palette is a deliberate two-color collapse of
// the gameplay palette:
//   🟦 ← correct-canonical only (locked correct — letter right AND move right)
//   🟧 ← correct-alt OR incorrect (right letter wrong move, OR wrong letter —
//        share grid's mental model is "this slot isn't locked in yet")
//   ⬛ ← empty (slot unfilled at this snapshot)
// Each row ends with a terminator (↩️ undo, ✅ win, ⬛ loss) so all rows share
// a visual width. See slotStates.ts for the full rationale of the gameplay
// three-way classification this collapses from.

import type { GameEvent } from './events'
import type { SlotState } from './slotStates'

export const SHARE_URL = 'https://letterflick.com' as const

const CORRECT_SLOT = '\u{1F7E6}' // 🟦 — letter matches target at this slot
const INCORRECT_SLOT = '\u{1F7E7}' // 🟧 — letter present but wrong
const EMPTY_SLOT = '\u{2B1B}' // ⬛ — slot empty at this snapshot (dark)
const UNDO = '\u{21A9}\u{FE0F}' // ↩️ (variation selector for emoji presentation)
// Final-row terminators. Both are single glyphs so the final row is the same
// visual length as undo rows — uniform-width rows scan as one block.
const WIN_END = '\u{2705}' // ✅ — final row on win (white check on green box)
const LOSS_END = '\u{2B1B}' // ⬛ — final row on loss (visually merges with
// empty slots on a losing row, which is fine — losing rows are mostly empty
// anyway, and the row's position at the end is the structural cue)

export interface ShareGridInput {
  readonly log: readonly GameEvent[]
  readonly wordLength: number
  readonly won: boolean
  readonly puzzleNumber: number
  // Target word for this puzzle, lowercase. Used per-slot to color squares
  // green vs. yellow.
  readonly targetWord: string
  // Final bar state at endgame, in the same lowercase form as
  // state.exitedLetters. Used to render the final row directly — preferred
  // over the in-walk simulated bar so the canonical end-state is what shows
  // up in the share grid even if the event log is somehow truncated.
  readonly finalBar: readonly string[]
  // Phase 9: per-slot in-game classification for the final-row render.
  // Parallel-indexed with finalBar; same source of truth as the live bar
  // renderer (render.ts). When omitted the renderer falls back to letter
  // equality with the target — preserving the pre-Phase-9 collapse of
  // correct-canonical and correct-alt into a single 🟦 square.
  readonly finalSlotStates?: readonly SlotState[]
  // Optional override for the share-text header line. When omitted, the
  // header is `Letter Flick #N`. Practice mode passes
  // `Letter Flick Practice #N` so shared practice grids identify as practice
  // runs — without conflating them with the daily puzzle number.
  readonly headerOverride?: string
}

export interface ShareGridOutput {
  // Full share text: header line, multi-line grid, URL line — joined with \n.
  // What gets passed to navigator.share / clipboard.
  readonly text: string
  // The grid rows joined with \n (no header, no URL). Used by tests / future
  // callers that want only the grid block.
  readonly gridOnly: string
  // Each row (with `↩️` suffix for undo rows; no terminator for the final
  // row). The modal renders one <span class="modal__grid-row"> per row so the
  // rows wrap correctly and can be styled independently. Kept ordered: undo
  // rows first (in player order), final-state row last.
  readonly gridRows: readonly string[]
  // Screen-reader description; mirrors the visible glyphs without requiring
  // the SR to interpret emoji. Wordle precedent for share-grid a11y.
  readonly aria: string
}

export function generateShareGrid(input: ShareGridInput): ShareGridOutput {
  const target = input.targetWord.toLowerCase()
  const wordLength = input.wordLength
  // Simulated bar — mirror of exitedLetters as the player played, used only
  // to snapshot the pre-undo state for each undo row.
  const bar: string[] = []
  // Parallel slot-state mirror. Each flick event since Phase 9 carries
  // `slotState`; older v4 events omit it (the resulting `undefined` in this
  // array triggers the letter-equality fallback in renderRow).
  const slotStates: (SlotState | undefined)[] = []
  const rows: string[] = []

  for (const ev of input.log) {
    if (ev.type === 'flick') {
      bar.push(ev.letter)
      slotStates.push(ev.slotState)
      continue
    }
    // undo: snapshot the bar BEFORE the pop — this is what the player saw on
    // screen right before they hit undo.
    rows.push(renderRow(bar, slotStates, target, wordLength, UNDO))
    // Loop continues — outer for…of iterates the event log; this push captures
    // the pre-undo bar state as the player saw it before tapping undo.
    // Tolerate (theoretical) over-undo: a defensive guard, since the
    // controller's undo gate should prevent this from ever happening in
    // practice.
    if (bar.length > 0) {
      bar.pop()
      slotStates.pop()
    }
  }

  // Final row uses the canonical end-state from the caller, not the
  // simulated bar. They should match for any well-formed log; preferring
  // finalBar keeps the final row honest even if the log is truncated. The
  // final-row terminator is a trophy on win, a black square on loss — same
  // glyph-count as the undo rows so all rows share a visual width.
  const finalEnd = input.won ? WIN_END : LOSS_END
  const finalStates = input.finalSlotStates ?? []
  rows.push(renderRow(input.finalBar, finalStates, target, wordLength, finalEnd))

  const gridOnly = rows.join('\n')
  const header =
    input.headerOverride !== undefined
      ? input.headerOverride
      : `Letter Flick #${String(input.puzzleNumber)}`
  const text = `${header}\n${gridOnly}\n${SHARE_URL}`
  const aria = buildAria(rows, input.won)

  return { text, gridOnly, gridRows: rows, aria }
}

// Render one row: per-slot 🟦/🟧/⬛ squares followed by a single-glyph
// terminator (↩️ for undo rows, ✅ for final-win, ⬛ for final-loss). All rows
// end up the same visual width, which makes the grid scan cleanly. Per-slot
// glyph selection is delegated to shareGlyphForSlot below.
function renderRow(
  bar: readonly string[],
  slotStates: readonly (SlotState | undefined)[],
  target: string,
  wordLength: number,
  terminator: string,
): string {
  const slots: string[] = []
  for (let i = 0; i < wordLength; i += 1) {
    slots.push(shareGlyphForSlot(bar[i], slotStates[i], target[i]))
  }
  return `${slots.join('')}${terminator}`
}

// Two-color contract for the share grid, codified as a single decision tree.
//
// Per-slot color comes from the in-game `SlotState` carried on the flick event
// (Phase 9). Only `correct-canonical` paints 🟦; both `correct-alt` (right
// letter, wrong move — puzzle is unsolvable from that state) and `incorrect`
// (wrong letter for this slot) paint 🟧, matching the user's "this slot isn't
// locked in" mental model for the share grid. Legacy v4 events without
// `slotState` fall back to letter-equality with the target — the pre-Phase-9
// behavior, present only for persisted-from-pre-Phase-9 logs.
function shareGlyphForSlot(
  letter: string | undefined,
  slotState: SlotState | undefined,
  want: string | undefined,
): string {
  if (letter === undefined) return EMPTY_SLOT
  if (slotState === 'correct-canonical') return CORRECT_SLOT
  if (slotState === 'correct-alt') return INCORRECT_SLOT
  if (slotState === 'incorrect') return INCORRECT_SLOT
  // 'empty' is structurally unreachable from current callers but present for exhaustive switch.
  if (slotState === 'empty') return EMPTY_SLOT
  // slotState === undefined: legacy v4 fallback. Compare letter to target
  // directly; collapses correct-canonical and correct-alt into 🟦, which is
  // the pre-Phase-9 behavior.
  return want !== undefined && letter === want ? CORRECT_SLOT : INCORRECT_SLOT
}

function buildAria(rows: readonly string[], won: boolean): string {
  const totalRows = rows.length
  const undoCount = countRowsWith(rows, UNDO)
  const finalRow = rows.length > 0 ? rows[rows.length - 1] : undefined
  // For the loss-end terminator, ⬛ also appears as the EMPTY_SLOT glyph; we
  // subtract one from the empty count when the final row is a loss so the
  // terminator isn't double-counted.
  const finalCorrect = finalRow === undefined ? 0 : countGlyphs(finalRow, CORRECT_SLOT)
  const finalIncorrect = finalRow === undefined ? 0 : countGlyphs(finalRow, INCORRECT_SLOT)
  const rawEmpty = finalRow === undefined ? 0 : countGlyphs(finalRow, EMPTY_SLOT)
  const finalEmpty = won ? rawEmpty : Math.max(0, rawEmpty - 1)
  const outcome = won ? 'Solved' : 'Unsolved'
  const snapNoun = totalRows === 1 ? 'snapshot' : 'snapshots'
  const undoFragment =
    undoCount === 0
      ? ''
      : `, including ${String(undoCount)} ${undoCount === 1 ? 'undo' : 'undos'}`
  const finalDesc = describeFinal(finalCorrect, finalIncorrect, finalEmpty)
  return `${outcome} in ${String(totalRows)} ${snapNoun}${undoFragment}. Final row: ${finalDesc}.`
}

function describeFinal(correct: number, incorrect: number, empty: number): string {
  const parts: string[] = []
  if (correct > 0) {
    parts.push(`${String(correct)} blue ${correct === 1 ? 'square' : 'squares'}`)
  }
  if (incorrect > 0) {
    parts.push(`${String(incorrect)} orange ${incorrect === 1 ? 'square' : 'squares'}`)
  }
  if (empty > 0) {
    parts.push(`${String(empty)} empty ${empty === 1 ? 'slot' : 'slots'}`)
  }
  return parts.length === 0 ? 'empty' : parts.join(', ')
}

// Count rows that contain `glyph`. Cheap; only used for the ARIA undo tally.
function countRowsWith(rows: readonly string[], glyph: string): number {
  let n = 0
  for (const r of rows) {
    if (r.includes(glyph)) n += 1
  }
  return n
}

// Count glyph occurrences. Uses Array.from-style iteration via for…of so
// multi-code-point emoji sequences are handled correctly. Cheap; only used
// for the ARIA description.
function countGlyphs(s: string, glyph: string): number {
  let n = 0
  let idx = 0
  while (true) {
    const found = s.indexOf(glyph, idx)
    if (found === -1) return n
    n += 1
    idx = found + glyph.length
  }
}
